import React, { useEffect, useState } from 'react';
import {
  Drawer, Descriptions, Tag, Space, Button, DatePicker, Input, Typography, Divider,
  message, Popconfirm, Tabs, Timeline, Empty, Spin, List,
} from 'antd';
import {
  PhoneOutlined, WhatsAppOutlined, EnvironmentOutlined, EditOutlined, CheckOutlined,
  CalendarOutlined, EyeOutlined, FileTextOutlined, PlusOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  fetchEventDetail, rescheduleEvent, completeEvent, addEventNote, deleteCalendarEvent,
} from '../services/calendarService';
import { PRIORITY_COLORS, STATUS_COLORS, BASE_TYPE_COLORS } from './calendarConstants';

const { Text, Paragraph } = Typography;
const fmt = (d) => (d ? dayjs(d).format('DD MMM YYYY, hh:mm A') : '—');

// Rich side panel for any calendar event (follow-up or custom).
const EventDrawer = ({ open, event, onClose, onChanged, onEditCustom }) => {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reschedule, setReschedule] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !event) return;
    setDetail(null); setReschedule(null); setNote('');
    setLoading(true);
    fetchEventDetail(event.source, event.id)
      .then(setDetail).catch(() => message.error('Failed to load event'))
      .finally(() => setLoading(false));
  }, [open, event]);

  if (!event) return null;
  const ev = (detail && detail.event) || event;
  const lead = detail && detail.lead;
  const client = detail && detail.client;
  const phone = (ev.CustomerMobile || '').replace(/[^0-9]/g, '');
  const address = (lead && lead.Address) || (detail && detail.raw && detail.raw.Address) || ev.City || '';
  const isPending = ev.Status === 'Pending' || ev.Status === 'Overdue';

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); message.success(ok); onChanged && onChanged(); }
    catch { message.error('Action failed'); }
    finally { setBusy(false); }
  };

  const doReschedule = async () => {
    if (!reschedule) { message.warning('Pick a new date/time'); return; }
    await run(() => rescheduleEvent(ev.source, ev.id, reschedule.format('YYYY-MM-DD HH:mm:ss')), 'Rescheduled');
    setReschedule(null);
  };
  const doNote = async () => {
    if (!note.trim()) return;
    await run(() => addEventNote(ev.source, ev.id, note.trim()), 'Note added');
    setNote('');
    fetchEventDetail(ev.source, ev.id).then(setDetail).catch(() => {});
  };

  const quickActions = (
    <Space wrap style={{ marginBottom: 12 }}>
      <Button size="small" icon={<PhoneOutlined />} href={`tel:${ev.CustomerMobile}`} disabled={!ev.CustomerMobile}>Call</Button>
      <Button size="small" icon={<WhatsAppOutlined style={{ color: '#25D366' }} />} href={`https://wa.me/${phone}`} target="_blank" disabled={!phone}>WhatsApp</Button>
      <Button size="small" icon={<EnvironmentOutlined />} href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`} target="_blank" disabled={!address}>Maps</Button>
      {isPending && <Button size="small" type="primary" icon={<CheckOutlined />} loading={busy}
        onClick={() => run(() => completeEvent(ev.source, ev.id), 'Marked completed')}>Complete</Button>}
      {ev.source === 'custom' && <Button size="small" icon={<EditOutlined />} onClick={() => onEditCustom && onEditCustom(detail && detail.raw)}>Edit</Button>}
      {ev.LeadID && <Button size="small" icon={<EyeOutlined />} onClick={() => { onClose(); navigate(`/leads/${ev.LeadID}`); }}>View Lead</Button>}
      {ev.ClientID && <Button size="small" icon={<EyeOutlined />} onClick={() => { onClose(); navigate('/clients'); }}>View Client</Button>}
      {ev.source === 'custom' && (
        <Popconfirm title="Delete this event?" okText="Delete" okButtonProps={{ danger: true }}
          onConfirm={() => run(async () => { await deleteCalendarEvent(ev.id); onClose(); }, 'Deleted')}>
          <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
        </Popconfirm>
      )}
    </Space>
  );

  const overview = (
    <>
      <Descriptions column={1} size="small" bordered labelStyle={{ width: 130 }}>
        <Descriptions.Item label="Title">{ev.Title}</Descriptions.Item>
        <Descriptions.Item label="Type"><Tag color={BASE_TYPE_COLORS[ev.EventType] || 'blue'}>{ev.EventType}</Tag></Descriptions.Item>
        <Descriptions.Item label="Status"><Tag color={STATUS_COLORS[ev.Status] || 'default'}>{ev.Status}</Tag></Descriptions.Item>
        <Descriptions.Item label="When">{fmt(ev.EventAt)}{ev.EndAt ? ` – ${fmt(ev.EndAt)}` : ''}</Descriptions.Item>
        <Descriptions.Item label="Customer">{ev.CustomerName || '—'}</Descriptions.Item>
        <Descriptions.Item label="Mobile">{ev.CustomerMobile || '—'}</Descriptions.Item>
        <Descriptions.Item label="City">{ev.City || '—'}</Descriptions.Item>
        <Descriptions.Item label="Priority">{ev.Priority ? <Tag color={PRIORITY_COLORS[ev.Priority] || 'default'}>{ev.Priority}</Tag> : '—'}</Descriptions.Item>
        <Descriptions.Item label="Executive">{ev.ExecutiveName || '—'}</Descriptions.Item>
        {lead && <Descriptions.Item label="Property">{lead.PropertyType || '—'}</Descriptions.Item>}
        <Descriptions.Item label="Address">{address || '—'}</Descriptions.Item>
        <Descriptions.Item label="Notes">{ev.Notes || '—'}</Descriptions.Item>
      </Descriptions>

      {isPending && (
        <>
          <Divider orientation="left" plain><CalendarOutlined /> Reschedule</Divider>
          <Space>
            <DatePicker showTime value={reschedule} onChange={setReschedule} format="DD MMM YYYY, hh:mm A" placeholder="New date & time" />
            <Button type="primary" loading={busy} onClick={doReschedule}>Update</Button>
          </Space>
        </>
      )}

      <Divider orientation="left" plain>Add Note</Divider>
      <Space.Compact style={{ width: '100%' }}>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Write a note…" onPressEnter={doNote} />
        <Button type="primary" icon={<PlusOutlined />} loading={busy} onClick={doNote}>Add</Button>
      </Space.Compact>
    </>
  );

  const history = (
    <>
      {(detail?.history || []).length === 0 && (detail?.timeline || []).length === 0 ? (
        <Empty description="No history yet" />
      ) : (
        <Timeline
          items={[
            ...(detail?.history || []).map(h => ({
              color: h.Action === 'Completed' ? 'green' : h.Action === 'Rescheduled' ? 'orange' : 'blue',
              children: <div><Text strong>{h.Action}</Text>{h.Detail && <div><Text type="secondary">{h.Detail}</Text></div>}<Text type="secondary" style={{ fontSize: 11 }}>{fmt(h.createdAt)}</Text></div>,
            })),
            ...(detail?.timeline || []).map(t => ({
              color: 'gray',
              children: <div><Text>{t.Title}</Text>{t.Description && <div><Text type="secondary">{t.Description}</Text></div>}<Text type="secondary" style={{ fontSize: 11 }}>{fmt(t.createdAt)}</Text></div>,
            })),
          ]}
        />
      )}
    </>
  );

  const followupsTab = (
    (detail?.followups || []).length === 0 ? <Empty description="No previous follow-ups" /> : (
      <List size="small" dataSource={detail.followups} renderItem={(f) => (
        <List.Item>
          <List.Item.Meta
            title={<Text>{f.CallStatus || f.FollowupType} {f.Outcome && <Tag color="blue">{f.Outcome}</Tag>}</Text>}
            description={<Text type="secondary" style={{ fontSize: 12 }}>{fmt(f.CallAt || f.createdAt)} · {f.ExecutiveName || '—'}{f.Notes ? ` · ${f.Notes}` : ''}</Text>}
          />
        </List.Item>
      )} />
    )
  );

  const documentsTab = (
    (detail?.documents || []).length === 0
      ? <Empty image={<FileTextOutlined style={{ fontSize: 36, color: '#d9d9d9' }} />} description="No documents" />
      : <List size="small" dataSource={detail.documents} renderItem={(d) => (
        <List.Item><List.Item.Meta title={d.OriginalName} description={d.Category} /></List.Item>
      )} />
  );

  return (
    <Drawer title={ev.Title || 'Event'} open={open} onClose={onClose} width={520}>
      {loading && !detail ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div> : (
        <>
          {quickActions}
          <Tabs
            defaultActiveKey="overview"
            items={[
              { key: 'overview', label: 'Overview', children: overview },
              { key: 'followups', label: 'Follow-up History', children: followupsTab },
              { key: 'documents', label: 'Documents', children: documentsTab },
              { key: 'history', label: 'Activity', children: history },
            ]}
          />
        </>
      )}
    </Drawer>
  );
};

export default EventDrawer;
